// Banco BASE "Dispersión de Pagos" fixed-width file generator.
//
// Spec: Downloads/BASE Dispersion/0000eldp.pdf — one operation per line, every
// line EXACTLY 232 characters. Used to mass-pay service providers from the
// "Por Pagar" list in /services.
//
// Line layout (1-indexed positions → field widths sum to 232):
//   1-2     Tipo operación   "01" SPEI (other banks) / "04" Terceros Banco BASE
//   3-32    Nombre/Alias     30  alfanum, left-aligned, space-padded
//   33-80   Cuenta destino   48  CLABE left-aligned, space-padded
//   81-94   Importe          14  numeric (12+2), right-aligned, zero-padded
//   95-124  Ref. numérica    30  right-aligned; for SPEI ≤7 numeric chars
//   125-154 Concepto         30  alfanum, left-aligned, space-padded
//   155-184 Referencia       30  only for 02 OPI — blank otherwise
//   185-187 Divisa           3   "MXN"
//   188-201 IVA              14  numeric (12+2), right-aligned, zero-padded
//   202-214 RFC              13  alfanum, left-aligned, space-padded
//   215-232 Cuenta cargo     18  numeric, the registered Banco BASE source acct
//
// Filename: YYYYMMDD + {count:3} + {consecutivo:2} + "DP" + ".txt"

const BASE_BANK_PREFIX = "145"; // CLABE prefix for Banco BASE → operation type 04

// Uppercase, strip accents and anything that isn't a letter/digit/space — the
// spec requires "Mayúsculas sin caracteres especiales ni acentos".
function clean(s) {
  return String(s == null ? "" : s)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
const onlyDigits = (s) => String(s == null ? "" : s).replace(/\D/g, "");
const fieldLeft = (s, n) => clean(s).slice(0, n).padEnd(n, " ");
const fieldRightSpaces = (s, n) => String(s == null ? "" : s).slice(0, n).padStart(n, " ");
// numeric importe in centavos, 14 digits zero-padded (12 enteros + 2 decimales)
const money14 = (amount) =>
  String(Math.round((Number(amount) || 0) * 100)).padStart(14, "0").slice(-14);

// Build a single 232-char line for one service payment.
export function buildDispersionLine({ provider, invoiceId, serviceId, amount, cuentaCargo }) {
  const clabe = onlyDigits(provider?.clabe);
  const rfc = clean(provider?.rfc);
  const tipo = clabe.startsWith(BASE_BANK_PREFIX) ? "04" : "01";
  const iva = Math.round((Number(amount) - Number(amount) / 1.16) * 100) / 100;
  const refNum = onlyDigits(serviceId).padStart(7, "0").slice(-7); // ≤7 numeric for SPEI

  const line =
    tipo +                                  // 1-2
    fieldLeft(provider?.name, 30) +         // 3-32
    clabe.padEnd(48, " ").slice(0, 48) +    // 33-80
    money14(amount) +                       // 81-94
    fieldRightSpaces(refNum, 30) +          // 95-124
    fieldLeft(invoiceId, 30) +              // 125-154
    " ".repeat(30) +                        // 155-184 (referencia: OPI only)
    "MXN" +                                 // 185-187
    money14(iva) +                          // 188-201
    fieldLeft(rfc, 13) +                    // 202-214
    onlyDigits(cuentaCargo).padStart(18, "0").slice(-18); // 215-232

  return line;
}

// Validate one service for dispersion; returns array of human-readable problems.
export function validateServiceForDispersion(s) {
  const reasons = [];
  const clabe = onlyDigits(s.provider?.clabe);
  if (clabe.length !== 18) reasons.push(`CLABE inválida (${clabe.length || 0} dígitos, deben ser 18)`);
  if (!clean(s.provider?.rfc)) reasons.push("RFC faltante");
  if (!(Number(s.total) > 0)) reasons.push("Importe inválido");
  return reasons;
}

// Build the whole file. `services` = rows with { id, invoice_id, total, provider }.
// `dateYmd` = "YYYYMMDD" execution date; `cuentaCargo` = 18-digit source account.
export function buildDispersionFile(services, { cuentaCargo, dateYmd, consecutivo = 1 }) {
  const included = [];
  const skipped = [];

  for (const s of services) {
    const reasons = validateServiceForDispersion(s);
    if (reasons.length) {
      skipped.push({ service: s, reasons });
      continue;
    }
    included.push({
      service: s,
      line: buildDispersionLine({
        provider: s.provider,
        invoiceId: s.invoice_id,
        serviceId: s.id,
        amount: s.total,
        cuentaCargo,
      }),
    });
  }

  // Each line is 232 chars; bank files are CRLF-terminated.
  const content = included.map((x) => x.line).join("\r\n") + (included.length ? "\r\n" : "");
  const filename = `${dateYmd}${String(included.length).padStart(3, "0")}${String(
    consecutivo
  ).padStart(2, "0")}DP.txt`;

  const total = included.reduce((sum, x) => sum + (Number(x.service.total) || 0), 0);
  return { content, filename, included, skipped, total };
}
